import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import {
  getMyTeamPermissions,
} from '../services/api.ts';
import { Mail, Lock, Loader2, Eye, EyeOff, ArrowRight } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext.tsx';
import { getDefaultAuthorizedPath, setUserPermissions } from '../services/permissionService.ts';
import { getSafeMcpReturnTo } from '../utils/mcpAuthorization.ts';
import { useAuth } from '../contexts/AuthContext.tsx';

export { getSafeMcpReturnTo };

const Login = () => {
  const { isDark } = useTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, status: authStatus, session } = useAuth();
  const isBusy = isLoading || authStatus === 'loading';
  const mcpReturnTo = getSafeMcpReturnTo(location.search);

  useEffect(() => {
    const savedNotice = localStorage.getItem('auth_notice_message');
    if (savedNotice) { setNotice(savedNotice); localStorage.removeItem('auth_notice_message'); }
    const savedError = localStorage.getItem('auth_error_message');
    if (savedError) {
      setError(savedError);
      localStorage.removeItem('auth_error_message');
    }
  }, []);

  useEffect(() => {
    if (authStatus === 'authenticated' && !isLoading) {
      navigate(session?.mustChangePassword ? `/primeiro-acesso${mcpReturnTo ? `?returnTo=${encodeURIComponent(mcpReturnTo)}` : ''}` : (mcpReturnTo || getDefaultAuthorizedPath()), { replace: true });
    }
  }, [authStatus, isLoading, mcpReturnTo, navigate, session?.mustChangePassword]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (authStatus === 'loading') return;
    setError('');
    setIsLoading(true);

    try {
      const authenticatedSession = await signIn(email, password);
      setPassword('');
      if (authenticatedSession.mustChangePassword) {
        navigate(`/primeiro-acesso${mcpReturnTo ? `?returnTo=${encodeURIComponent(mcpReturnTo)}` : ''}`, { replace: true });
        return;
      }

      try {
        const teamPermissions = await getMyTeamPermissions();
        setUserPermissions(
          teamPermissions.menu_permissions,
          teamPermissions.contact_funnel_stages,
          teamPermissions.menu_permissions_mode,
        );
      } catch {
        // O login continua com o fallback de permissões local.
      }

      navigate(mcpReturnTo || getDefaultAuthorizedPath(), { replace: Boolean(mcpReturnTo) });
    } catch (err: any) {
      setError(err.code === 'temporary_password_expired' ? 'A senha temporária expirou. Peça ao administrador da clínica para reenviar o acesso.' : err.message || 'Falha ao fazer login');
    } finally {
      setIsLoading(false);
    }
  }

  // Penpot: superfícies estáticas e botões sem sombra decorativa.
  const bg = isDark ? 'bg-[#08111F]' : 'bg-sminy-canvas';
  const cardBg = isDark ? 'bg-[#0F1D31] border-[#344054]' : 'bg-white border-sminy-border';
  const textPrimary = isDark ? 'text-[#F8FAFC]' : 'text-sminy-text-primary';
  const textSecondary = isDark ? 'text-[#D0D5DD]' : 'text-sminy-text-secondary';
  const textMuted = isDark ? 'text-[#98A2B3]' : 'text-sminy-muted';
  const labelText = textSecondary;
  const inputBg = isDark
    ? 'bg-[#0F1D31] border-[#344054] text-[#F8FAFC] placeholder-[#98A2B3]'
    : 'bg-white border-sminy-border text-sminy-text-primary placeholder-sminy-muted';
  const iconIdle = textMuted;
  const dividerBorder = isDark ? 'border-[#344054]' : 'border-sminy-border';

  return (
    <div className={`relative min-h-screen w-full ${bg}`} style={{ fontFamily: "'Lexend Deca', sans-serif" }}>
      <main className="relative z-10 flex min-h-screen w-full items-center justify-center px-4 py-8 pb-[max(2rem,env(safe-area-inset-bottom))] pt-[max(2rem,env(safe-area-inset-top))] sm:px-6 sm:py-10">
        <div className="w-full max-w-[440px]">
          {/* Logo + heading */}
          <div className="mb-8 text-center">
            <img
              src={isDark ? '/logo-sminy-white.png' : '/logo-sminy.png'}
              alt="Sminy"
              className="mx-auto mb-6 h-12 w-auto"
            />
            <h1 className={`text-2xl font-semibold ${textPrimary}`}>
              Entre na sua conta
            </h1>
            <p className={`mt-2 text-sm ${textSecondary}`}>
              {mcpReturnTo
                ? 'Entre para revisar a conexão segura com a Sminy'
                : 'Plataforma de IA para clínicas odontológicas'}
            </p>
          </div>

          {/* Card de login */}
          <div
            className={`relative rounded-2xl border p-6 shadow-none sm:p-8 ${cardBg}`}
          >
            {notice && <p role="status" className="mb-4 text-sm text-sminy-primary">{notice}</p>}
            {error && (
              <div
                role="alert"
                className={`mb-6 flex items-start gap-3 rounded-xl border px-4 py-3 text-sm animate-in fade-in slide-in-from-top-2 ${
                  isDark
                    ? 'border-red-500/20 bg-red-500/10 text-red-300'
                    : 'border-red-200 bg-red-50 text-red-600'
                }`}
              >
                <svg className="mt-0.5 h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="leading-relaxed">{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email */}
              <div className="space-y-2">
                <label
                  htmlFor="email"
                  className={`block text-sm font-medium ${labelText}`}
                >
                  Email
                </label>
                <div className="group relative">
                  <div
                    className={`pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 transition-colors group-focus-within:text-sminy-primary ${iconIdle}`}
                  >
                    <Mail className="h-[18px] w-[18px]" strokeWidth={1.75} />
                  </div>
                  <input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    className={`block h-11 sm:h-10 w-full rounded-xl border px-3.5 pl-11 text-sm transition-all duration-150 focus:border-sminy-primary/60 focus:outline-none focus:ring-2 focus:ring-sminy-primary/20 ${inputBg}`}
                    placeholder="voce@clinica.com"
                    disabled={isBusy}
                    autoComplete="email"
                  />
                </div>
              </div>

              {/* Senha */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label
                    htmlFor="password"
                    className={`block text-sm font-medium ${labelText}`}
                  >
                    Senha
                  </label>
                  <Link
                    to={`/recuperar-senha${mcpReturnTo ? `?returnTo=${encodeURIComponent(mcpReturnTo)}` : ''}`}
                    className="text-xs font-medium text-sminy-primary transition-colors hover:text-sminy-primary-hover"
                  >
                    Esqueci minha senha
                  </Link>
                </div>
                <div className="group relative">
                  <div
                    className={`pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 transition-colors group-focus-within:text-sminy-primary ${iconIdle}`}
                  >
                    <Lock className="h-[18px] w-[18px]" strokeWidth={1.75} />
                  </div>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className={`block h-11 sm:h-10 w-full rounded-xl border px-3.5 pl-11 pr-11 text-sm transition-all duration-150 focus:border-sminy-primary/60 focus:outline-none focus:ring-2 focus:ring-sminy-primary/20 ${inputBg}`}
                    placeholder="••••••••"
                    disabled={isBusy}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={`absolute inset-y-0 right-0 flex items-center w-11 justify-center rounded-xl transition-colors hover:text-sminy-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-sminy-primary ${iconIdle}`}
                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  >
                    {showPassword ? (
                      <EyeOff className="h-[18px] w-[18px]" strokeWidth={1.75} />
                    ) : (
                      <Eye className="h-[18px] w-[18px]" strokeWidth={1.75} />
                    )}
                  </button>
                </div>
              </div>

              {/* Lembrar */}
              <label className="flex cursor-pointer items-center gap-2.5 pt-1 select-none">
                <input
                  type="checkbox"
                  className={`h-4 w-4 cursor-pointer rounded text-sminy-primary focus:ring-2 focus:ring-sminy-primary/30 focus:ring-offset-0 ${
                    isDark ? 'border-white/20 bg-white/[0.04]' : 'border-slate-300'
                  }`}
                />
                <span className={`text-sm ${textSecondary}`}>Manter conectado</span>
              </label>

              {/* Botão Entrar */}
              <button
                type="submit"
                disabled={isBusy}
                className={`inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-sminy-primary px-4 text-sm font-medium text-white shadow-none transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-sminy-primary focus-visible:ring-offset-2 sm:h-10 ${
                  isBusy ? 'cursor-not-allowed opacity-[0.56]' : 'hover:bg-sminy-primary-hover active:bg-sminy-primary-pressed'
                }`}
              >
                {isBusy ? (
                  <>
                    <Loader2 className="h-[18px] w-[18px] animate-spin" />
                    <span>{authStatus === 'loading' ? 'Validando sessão...' : 'Entrando...'}</span>
                  </>
                ) : (
                  <>
                    <span>Entrar</span>
                    <ArrowRight className="h-[18px] w-[18px]" strokeWidth={2} />
                  </>
                )}
              </button>
            </form>
          </div>

          {/* CTA Trial — para usuários novos */}
          <div className="mt-8 flex flex-col items-center gap-1.5 text-center">
            <p className={`text-sm ${textSecondary}`}>
              Ainda não é cliente da Sminy?
            </p>
            <Link
              to="/register"
              className="group/cta inline-flex items-center gap-1.5 text-sm font-semibold text-sminy-primary transition-colors hover:text-sminy-primary-hover"
            >
              <span className="border-b border-sminy-primary/30 pb-px transition-colors group-hover/cta:border-sminy-primary-hover/60">
                Teste grátis por 14 dias
              </span>
              <ArrowRight
                className="h-4 w-4 transition-transform duration-200 group-hover/cta:translate-x-0.5"
                strokeWidth={2.25}
              />
            </Link>
          </div>

          {/* Divisor */}
          <div className={`mt-10 h-px w-full ${dividerBorder} border-t`} />

          {/* Footer */}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-1.5 gap-y-1 text-center">
            <p className={`text-xs ${textMuted}`}>
              © {new Date().getFullYear()} Sminy
            </p>
            <span className={`text-xs ${textMuted}`}>·</span>
            <a
              href="#"
              className={`text-xs transition-colors hover:text-sminy-primary ${textMuted}`}
            >
              Termos
            </a>
            <span className={`text-xs ${textMuted}`}>·</span>
            <a
              href="#"
              className={`text-xs transition-colors hover:text-sminy-primary ${textMuted}`}
            >
              Privacidade
            </a>
            <span className={`text-xs ${textMuted}`}>·</span>
            <a
              href="https://wa.me/552127559449?text=Ol%C3%A1%2C%20preciso%20de%20suporte"
              target="_blank"
              rel="noopener noreferrer"
              className={`text-xs transition-colors hover:text-sminy-primary ${textMuted}`}
            >
              Suporte
            </a>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Login;
